/**
 * La carte « Pointages » du bureau (registre B34, écran 7 de l'ébauche) : par jour et par technicien, le trajet, le temps sur site,
 * la pause (jamais facturée), la main d'œuvre facturée (temps sur site au palier supérieur) et le nombre de pointages, dont ceux
 * saisis après coup. Chaque ligne se déplie sur ses heures ; qui gère le planning peut « Corriger » une heure, supprimer un pointage
 * ou « Ajouter un pointage oublié ».
 *
 * Le refus d'un geste a son propre état (jamais effacé par la relecture qui suit) ; un conflit (409) relit l'écran PUIS le dit.
 */
import { useState } from 'react';

import { ApiError, api, type DestinationPointage, type GestePointage, type PointagesJour } from '../api/client';
import { GESTES_BUREAU, heureFr, heureLocale, instantDepuisHeure, libellePointage, lignesTableau, problemeAjout } from '../domaine/pointages';
import { jourFr } from '../domaine/planning';

/** Un membre d'un jour à qui le bureau peut ajouter un pointage oublié. */
export interface MembrePointable {
  readonly jour: string;
  readonly technicienId: string;
  readonly nom: string;
}

export function CartePointages({
  interventionId,
  pointages,
  membres,
  peutGerer,
  onChange,
}: {
  interventionId: string;
  pointages: readonly PointagesJour[];
  /** Les membres des jours (passés ou d'aujourd'hui) à qui ajouter un pointage oublié. */
  membres: readonly MembrePointable[];
  peutGerer: boolean;
  onChange: () => void;
}) {
  const [ouvertes, setOuvertes] = useState<ReadonlySet<string>>(new Set());
  const [correction, setCorrection] = useState<{ id: string; jour: string; heure: string } | null>(null);
  const [ajout, setAjout] = useState<{ cle: string; geste: GestePointage | ''; heure: string; destination: DestinationPointage | ''; travailFini: '' | 'oui' | 'non' } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  if (pointages.length === 0 && !(peutGerer && membres.length > 0)) return null;
  const lignes = lignesTableau(pointages);

  const basculer = (cle: string) =>
    setOuvertes((avant) => {
      const s = new Set(avant);
      if (s.has(cle)) s.delete(cle);
      else s.add(cle);
      return s;
    });

  const agir = async (geste: () => Promise<unknown>, reussite: string) => {
    setErreur(null);
    setSucces(null);
    setEnCours(true);
    try {
      await geste();
      setCorrection(null);
      setAjout(null);
      setSucces(reussite);
      onChange();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Geste impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const enregistrerCorrection = () => {
    if (!correction) return;
    const instant = instantDepuisHeure(correction.jour, correction.heure);
    if (instant === null) {
      setErreur('Écrivez l’heure comme 8:10 ou 8h10.');
      return;
    }
    void agir(() => api.corrigerPointage(interventionId, correction.id, { occurredAt: instant }), `Heure corrigée : ${heureFr(instant)} (saisie après coup).`);
  };

  const enregistrerAjout = () => {
    if (!ajout) return;
    const membre = membres.find((m) => `${m.jour}|${m.technicienId}` === ajout.cle);
    const probleme = problemeAjout({
      jour: membre?.jour ?? '',
      technicienId: membre?.technicienId ?? '',
      geste: ajout.geste,
      heure: ajout.heure,
      destination: ajout.destination,
      travailFini: ajout.travailFini,
    });
    if (probleme !== null || !membre || ajout.geste === '') {
      setErreur(probleme ?? 'Choisissez le jour et le technicien.');
      return;
    }
    const instant = instantDepuisHeure(membre.jour, ajout.heure) as string;
    const geste = ajout.geste;
    void agir(
      () =>
        api.ajouterPointage(interventionId, {
          jour: membre.jour,
          technicienId: membre.technicienId,
          geste,
          occurredAt: instant,
          ...(geste === 'LEAVE' ? { destination: ajout.destination as DestinationPointage, travailFini: ajout.travailFini === 'oui' } : {}),
        }),
      `Pointage ajouté pour ${membre.nom} le ${jourFr(membre.jour)} à ${heureFr(instant)} (saisi après coup).`,
    );
  };

  return (
    <div className="carte" id="pointages">
      <h2>Pointages</h2>
      <div className="carte-corps">
        {lignes.length === 0 ? (
          <div className="vide">Aucun pointage : les heures de cette intervention ont été saisies au bureau, ou ne le sont pas encore.</div>
        ) : (
          <div className="tableau-defilant">
            <table className="tableau-bon tableau-pointages">
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Technicien</th>
                  <th className="montant">Trajet</th>
                  <th className="montant">Sur site</th>
                  <th className="montant">Pause</th>
                  <th className="montant">MO facturée</th>
                  <th>Pointages</th>
                </tr>
              </thead>
              <tbody>
                {pointages.map((p, i) => {
                  const l = lignes[i]!;
                  const ouverte = ouvertes.has(l.cle);
                  return [
                    <tr key={l.cle}>
                      <td>{l.jour}</td>
                      <td>{l.technicien}</td>
                      <td className="montant">{l.trajet}</td>
                      <td className="montant">{l.surSite}</td>
                      <td className="montant">{l.pause}</td>
                      <td className="montant">{l.moFacturee}</td>
                      <td>
                        <button type="button" className="lien-document" aria-expanded={ouverte} onClick={() => basculer(l.cle)}>
                          {l.pointages} {ouverte ? '▾' : '▸'}
                        </button>
                      </td>
                    </tr>,
                    ouverte && (
                      <tr key={`${l.cle}-detail`} className="detail-pointages">
                        <td colSpan={7}>
                          <ul className="liste-pointages">
                            {p.evenements.map((e) => (
                              <li key={e.id}>
                                <span className="heure-pointage">{heureLocale(e.occurredAt)}</span> {libellePointage(e)}
                                {e.saisieApresCoup && <span className="pastille-en-plus">saisi après coup</span>}
                                {e.source === 'DESK' && <span className="aide"> (bureau)</span>}
                                {peutGerer &&
                                  (correction?.id === e.id ? (
                                    <span className="saisie-ligne">
                                      <input
                                        type="text"
                                        className="champ-court"
                                        aria-label="Nouvelle heure"
                                        value={correction.heure}
                                        disabled={enCours}
                                        onChange={(ev) => setCorrection({ ...correction, heure: ev.target.value })}
                                      />
                                      <button type="button" disabled={enCours} onClick={enregistrerCorrection}>
                                        Enregistrer
                                      </button>
                                      <button type="button" className="secondaire" disabled={enCours} onClick={() => setCorrection(null)}>
                                        Annuler
                                      </button>
                                    </span>
                                  ) : (
                                    <>
                                      {' '}
                                      <button
                                        type="button"
                                        className="secondaire discret"
                                        disabled={enCours}
                                        onClick={() => setCorrection({ id: e.id, jour: p.jour, heure: heureLocale(e.occurredAt) })}
                                      >
                                        Corriger
                                      </button>{' '}
                                      <button
                                        type="button"
                                        className="secondaire discret"
                                        disabled={enCours}
                                        onClick={() => {
                                          if (window.confirm(`Supprimer le pointage « ${libellePointage(e)} » de ${heureFr(e.occurredAt)} ? Il reste visible dans l’historique.`)) {
                                            void agir(() => api.corrigerPointage(interventionId, e.id, { annuler: true }), 'Pointage supprimé.');
                                          }
                                        }}
                                      >
                                        Supprimer
                                      </button>
                                    </>
                                  ))}
                              </li>
                            ))}
                          </ul>
                          {p.anomalies.length > 0 && <p className="aide">{p.anomalies.join(' ')}</p>}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}

        {peutGerer && membres.length > 0 && (
          <div className="saisie-ligne">
            {ajout === null ? (
              <button
                type="button"
                className="secondaire"
                disabled={enCours}
                onClick={() => setAjout({ cle: '', geste: '', heure: '', destination: '', travailFini: '' })}
              >
                + Ajouter un pointage oublié
              </button>
            ) : (
              <>
                <select aria-label="Jour et technicien" value={ajout.cle} onChange={(e) => setAjout({ ...ajout, cle: e.target.value })}>
                  <option value="">Jour et technicien…</option>
                  {membres.map((m) => (
                    <option key={`${m.jour}|${m.technicienId}`} value={`${m.jour}|${m.technicienId}`}>
                      {jourFr(m.jour)} — {m.nom}
                    </option>
                  ))}
                </select>
                <select aria-label="Geste" value={ajout.geste} onChange={(e) => setAjout({ ...ajout, geste: e.target.value as GestePointage | '' })}>
                  <option value="">Geste…</option>
                  {GESTES_BUREAU.map((g) => (
                    <option key={g.geste} value={g.geste}>
                      {g.libelle}
                    </option>
                  ))}
                </select>
                {ajout.geste === 'LEAVE' && (
                  <>
                    <select aria-label="Où" value={ajout.destination} onChange={(e) => setAjout({ ...ajout, destination: e.target.value as DestinationPointage | '' })}>
                      <option value="">Où…</option>
                      <option value="WORKSHOP">À l’atelier</option>
                      <option value="HOME">Chez lui</option>
                    </select>
                    <select aria-label="Travail" value={ajout.travailFini} onChange={(e) => setAjout({ ...ajout, travailFini: e.target.value as '' | 'oui' | 'non' })}>
                      <option value="">Travail…</option>
                      <option value="oui">Fini ici</option>
                      <option value="non">À reprendre</option>
                    </select>
                  </>
                )}
                <input
                  type="text"
                  className="champ-court"
                  aria-label="Heure du pointage"
                  placeholder="8:10"
                  value={ajout.heure}
                  onChange={(e) => setAjout({ ...ajout, heure: e.target.value })}
                />
                <button type="button" disabled={enCours} onClick={enregistrerAjout}>
                  Ajouter
                </button>
                <button type="button" className="secondaire" disabled={enCours} onClick={() => setAjout(null)}>
                  Fermer
                </button>
              </>
            )}
          </div>
        )}
        <p className="aide">
          Les heures se pointent sur le téléphone ; trajet et pause sont payés mais jamais facturés en main d’œuvre (le trajet est
          compris dans le déplacement). La main d’œuvre facturée est le temps sur site, pauses retirées, au palier supérieur.
        </p>
        {succes && <div className="message succes">{succes}</div>}
        {erreur && <div className="message erreur">{erreur}</div>}
      </div>
    </div>
  );
}
